/**
 * Comments Press Zone Admin SPA Entry Point
 */
import './css/main.scss';
import { el, mount, clear, __ } from './utils/dom.js';
import Toast from './components/Toast.js';
import Modal from './components/Modal.js';
import { showInfractionModal } from './components/InfractionModal.js';

// Set public path for dynamic chunks
if (window.presszoneCommentsAdmin?.assetsUrl) {
    // eslint-disable-next-line no-undef
    __webpack_public_path__ = window.presszoneCommentsAdmin.assetsUrl;
}

// Global state
let currentState = {
    page: 'dashboard',
    subPath: '',
    loading: false
};

/**
 * Sidebar Component
 */
function Sidebar({ items, activeId, onNavigate }) {
    return el('aside', { class: 'presszone-comments-sidebar' },
        el('div', { class: 'presszone-comments-sidebar__header' },
            el('h2', { class: 'presszone-comments-sidebar__title' }, __('Comments Zone', 'comments-press-zone')),
            el('span', { class: 'presszone-comments-sidebar__version' },
                window.presszoneCommentsAdmin?.version || 'v1.0.0'
            )
        ),
        el('nav', { class: 'presszone-comments-sidebar__nav' },
            ...items.map(item => el('a', {
                class: `presszone-comments-sidebar__link ${activeId === item.id ? 'presszone-comments-sidebar__link--active' : ''}`,
                'data-id': item.id,
                href: `#/${item.id}`,
                onclick: (e) => {
                    e.preventDefault();
                    console.log('[Sidebar] Link clicked:', item.id);
                    // Close mobile sidebar when navigating
                    document.body.classList.remove('presszone-comments-sidebar-open');
                    console.log('[Sidebar] Calling onNavigate with:', item.id);
                    onNavigate(item.id);
                }
            },
                el('span', { class: 'presszone-comments-sidebar__icon' }, item.icon),
                el('span', { class: 'presszone-comments-sidebar__label' }, item.label)
            ))
        )
    );
}

/**
 * Mobile Hamburger Button
 */
function HamburgerButton() {
    return el('button', {
        class: 'presszone-comments-hamburger',
        'aria-label': __('Toggle Menu', 'comments-press-zone'),
        onclick: () => document.body.classList.toggle('presszone-comments-sidebar-open')
    },
        el('span', { class: 'presszone-comments-hamburger__bar' }),
        el('span', { class: 'presszone-comments-hamburger__bar' }),
        el('span', { class: 'presszone-comments-hamburger__bar' })
    );
}

/**
 * Mobile Sidebar Overlay
 */
function SidebarOverlay() {
    return el('div', {
        class: 'presszone-comments-sidebar-overlay',
        onclick: () => document.body.classList.remove('presszone-comments-sidebar-open')
    });
}

/**
 * Main Application Shell
 */
function App() {
    const container = el('div', { class: 'presszone-comments-admin' });

    // Create sidebar with navigation
    const navItems = [
        { id: 'dashboard', label: __('Dashboard', 'comments-press-zone'), icon: '📊' },
        { id: 'moderation', label: __('Moderation', 'comments-press-zone'), icon: '🛡️' },
        { id: 'design', label: __('Design', 'comments-press-zone'), icon: '🎨' },
        { id: 'settings', label: __('Settings', 'comments-press-zone'), icon: '⚙️' },
        { id: 'tools', label: __('Tools', 'comments-press-zone'), icon: '🛠️' }
    ];

    const sidebar = Sidebar({
        items: navItems,
        activeId: currentState.page,
        onNavigate: (id) => {
            console.log('[App] onNavigate called with:', id);
            console.log('[App] Current hash before change:', window.location.hash);
            window.location.hash = `#/${id}`;
            console.log('[App] Hash set to:', window.location.hash);
        }
    });

    const mainContent = el('main', {
        class: 'presszone-comments-admin-main',
        id: 'presszone-comments-content'
    });

    // Global modal container - required for Modal component
    const modalContainer = el('div', { id: 'presszone-comments-modal-container' });

    // Mobile hamburger menu
    const hamburger = HamburgerButton();
    const overlay = SidebarOverlay();

    mount(container, hamburger);
    mount(container, overlay);
    mount(container, sidebar);
    mount(container, mainContent);
    mount(container, modalContainer);

    return container;
}

/**
 * Simple Hash Router
 */
async function router() {
    console.log('[Router] Called with hash:', window.location.hash);
    
    // Make addNotice globally available for pages and frontend use
    window.addNotice = (message, type = 'success') => Toast.show({ message, type });

    // Export Modal and InfractionModal globally for frontend use
    window.PresszoneCommentsModal = Modal;
    window.showInfractionModal = showInfractionModal;

    const root = document.getElementById('presszone-comments-admin-root');
    if (!root) {
        // Root element not found - likely loading on frontend or wrong page
        // Exit gracefully without error
        console.log('[Router] Root element not found');
        return;
    }

    const hash = window.location.hash.substring(2) || 'dashboard';
    console.log('[Router] Parsed hash:', hash);

    // Extract path before query parameters for routing
    const pathBeforeQuery = hash.split('?')[0];
    const [page, subPath] = pathBeforeQuery.split('/');
    
    console.log('[Router] Navigating to page:', page, 'subPath:', subPath);

    currentState.page = page;
    currentState.subPath = subPath || '';

    // Re-render shell if needed
    if (!document.getElementById('presszone-comments-content')) {
        console.log('[Router] Re-rendering shell');
        clear(root);
        mount(root, App());
    }

    const contentArea = document.getElementById('presszone-comments-content');
    if (!contentArea) {
        // This shouldn't happen - App() should have created the content area
        console.log('[Router] Content area not found after shell render');
        return;
    }

    // Update active state in sidebar
    const sidebarLinks = document.querySelectorAll('.presszone-comments-sidebar__link');
    sidebarLinks.forEach(link => {
        const id = link.getAttribute('data-id');
        if (id === page) {
            link.classList.add('presszone-comments-sidebar__link--active');
        } else {
            link.classList.remove('presszone-comments-sidebar__link--active');
        }
    });

    // Render page content
    try {
        switch (page) {
            case 'dashboard': {
                const { renderDashboard } = await import('./pages/dashboard.js');
                await renderDashboard(contentArea);
                break;
            }
            case 'moderation': {
                const { renderModeration } = await import('./pages/moderation.js');
                await renderModeration(contentArea, window.addNotice, subPath);
                break;
            }
            case 'design': {
                const { renderDesign } = await import('./pages/design.js');
                await renderDesign(contentArea, window.addNotice);
                break;
            }
            case 'settings': {
                const { renderSettings } = await import('./pages/settings.js');
                await renderSettings(contentArea, window.addNotice, subPath);
                break;
            }
            case 'tools': {
                const { renderTools } = await import('./pages/tools.js');
                await renderTools(contentArea, window.addNotice);
                break;
            }
            default:
                contentArea.innerHTML = `<h2>${__('Page Not Found', 'comments-press-zone')}</h2>`;
        }
        console.log('[Router] Page rendered successfully');
    } catch (err) {
        console.error('Comments Press Zone: Page render error', err);
        contentArea.innerHTML = `<div class="presszone-comments-error"><h2>Render Error</h2><p>${err.message}</p></div>`;
    }
}

// Initialize
window.addEventListener('hashchange', router);

if (document.readyState === 'complete' || document.readyState === 'interactive') {
    router();
} else {
    document.addEventListener('DOMContentLoaded', router);
}
